// Non-generated frontend API layer: label/formatter helpers plus the few types
// that have no backend schema to generate from.
//
// Most API types come from the orval-generated `./generated/model`. What lives
// here instead:
//   - the operator-facing label / formatter / tone helpers (pure UI vocabulary),
//   - the worker-contract types (`CartProgress`, `CartSummary`) that mirror a
//     run's free-form `progress` / `summary` dict (deliberately untyped on the
//     backend, FRD 3.4 - so `RunOut.progress` generates as `unknown`), and
//   - thin refinements (`Run`, `Job`) that narrow the generated `RunOut` /
//     `JobOut` where the frontend needs the richer shape.

import type { JobOut, RunOut } from "./generated/model";

export type { Platform, Flow } from "./generated/model";
import type { Flow, FlowCategory, Platform } from "./generated/model";

export type RunStatus =
  | "queued"
  | "claimed"
  | "running"
  | "succeeded"
  | "degraded"
  | "failed"
  | "cancelled"
  | "timed_out";

export type Trigger = "manual" | "scheduled";

// Positional geometry aliases (the generated types leave these as number[] /
// unknown[]; the frontend indexes them positionally).
export type LatLon = [number, number];
// [lat, lon, tier] and, on the store-detail read, two extra fields the overlap view uses:
// serving_count (stores serving this cell, incl. this one, >= 1) and the co-serving store
// ids (excluding this store). Optional so the plain triples other endpoints emit still fit.
export type StoreCell = [number, number, string, number?, string[]?];

// A job stores only the scope keys its flow reads, and every one of those is present:
// its "all" literal or a non-empty array of names. Optional here because the keys vary
// by flow, not because a flow may leave one out.
export interface JobInputs {
  // Cart, shelf-id, brand sweep, SKU match, taxonomy classify.
  brand_scope?: "all" | string[];
  // Every flow with a store axis: cart, carriage map, shelf-id, brand sweep, rank.
  store_scope?: "all_active" | string[];
  // Brand sweep: the search terms to run.
  candidate_scope?: "all" | string[];
  // Search rank / category rank: the registry entries to measure.
  keyword_scope?: "all" | string[];
  category_scope?: "all" | string[];
  // Cart-inventory only: scope the sweep to the store_sku_map carriage matrix (probe
  // only proven-carried store x SKU pairs) instead of the full cartesian.
  use_store_sku_map?: boolean;
  // Taxonomy classify only: the node the taxonomy page's reclassify launch named, a
  // prompt hint rather than a filter. Only that launch writes it; the job form keeps it.
  emphasis_node_id?: string | null;
}

// All counters optional ints; render whatever the engine emits.
export interface CartProgress {
  stores_total?: number;
  stores_processed?: number;
  stores_not_serviceable?: number;
  // Store-SKU coverage - each unit is one (dark store, SKU) check
  store_sku_total?: number;
  store_sku_giveup?: number;
  store_sku_unresolved_id?: number;
  store_sku_not_serviceable?: number;
  store_sku_delisted?: number;
  store_sku_delisted_saved_absence?: number;
  store_sku_delisted_pdp_absence?: number;
  store_sku_resumed?: number; // pairs restored from the per-run resume cache (no re-probe)
  rows_emitted?: number;
  rows_deduped?: number;
  rotations?: number;
  acquire_denied?: number;
  // Cart only: batches the serve gate refused after walking its whole IP budget without
  // priming. Split out of acquire_denied because they mean opposite things - a denial is
  // the pool having nothing free, this is every IP it tried refusing to prime.
  prime_unavailable?: number;
  bytes_up?: number;
  bytes_down?: number;
  // per-fetch outcome counts (the "churn" view)
  batches_ok?: number;
  batches_empty?: number;
  batches_not_serviceable?: number;
  batches_skipped?: number;
  batches_rate_limited?: number;
  batches_blocked?: number;
  batches_transport_error?: number;
  batches_timeout?: number;
  batches_other_4xx?: number;
  // CONNECTs our proxy provider (Webshare) refused (402 out of credit / 407 bad
  // credentials). Non-zero means the proxy vendor account is the problem, not the
  // target and not the roster.
  batches_proxy_provider_denied?: number;
  batches_requeued?: number;
  // Batches that succeeded but whose session the target refused part-way, standing the
  // worker down. Not a failure bucket: the batch is an OK and its lines were written. It
  // is the rate-limit pressure a run is actually under, which the failure counters
  // understate -- they only see the refusals that killed a whole batch. Non-zero and
  // rising means the run is above the target's fleetwide ceiling: lower concurrency.
  batches_session_spent?: number;
  // -- the request axis (cart flows) --------------------------------------------
  // Everything above counts BATCHES (work items); these count the HTTP requests those
  // batches spent on the target -- fan-out and the out-of-band prime hunt included -- so a
  // per-second request rate and a block rate are written in the unit the target rate-limits
  // in. Absent on runs that predate the counters, which reads as "no request panel".
  http_requests?: number;
  // Of ``http_requests``, the ones that established the SESSION rather than doing a
  // batch's work: bootstrap, routing, pinning, binding. A subset, never additional. They
  // are a per-LEASE cost amortised across every batch that lease goes on to serve, so
  // charging them to one batch reads as a fan-out that batch never had - which is why
  // calls-per-batch nets them out and shows them on a line of their own instead.
  http_requests_setup?: number;
  // 5xx responses, counted off the raw status: a 5xx has no CartOutcome of its own (the
  // cart ladder folds it into transport_error so the IP rotates), so without this it reads
  // in the churn panel as a dead tunnel.
  http_5xx?: number;
  // ATTEMPT latency: one timing per attempt, covering however many requests it fanned out
  // to. Totals for the mean, a fixed-edge histogram for the percentiles.
  // The histogram is keyed "<outcome class>|<edge ms>" (plus "inf" for the tail), and the
  // per-class ms totals beside it give each class its own mean -- a slow 200 (the target
  // throttling) and a fast 403 (the target refusing) are opposite findings, and one
  // blended mean is a middle number that describes neither.
  latency_ms_total?: number;
  latency_samples?: number;
  latency_ms_max?: number;
  latency_buckets_ms?: Record<string, number>;
  latency_ms_total_by_class?: Record<string, number>;
  // Per-pair attempt economics over ANSWERED (store, SKU) pairs: {bucket: pairs} plus the
  // attempts they cost in total. Answers only -- a pair that never got one is a give-up,
  // and folding the two together reports the cost of failure as the cost of success.
  answers_by_attempts?: Record<string, number>;
  answer_attempts_total?: number;
  // The exit-node shape of the run: how many IPs it used and how they split by their own
  // refusal share. This is the question a block rate cannot answer -- whether the refusals
  // are a few hot nodes (rotate) or the whole roster (the target is refusing everyone).
  proxy_ips_used?: number;
  proxy_ips_clean?: number;
  proxy_ips_degraded?: number;
  proxy_ips_burned?: number;
  // debug captures kept vs dropped by per-error-class sampling (cart + merchant).
  // Absent on runs that predate sampling.
  debug_captured?: number;
  debug_sampled_out?: number;
  // brand sweep (Phase 6): search -> PDP + read-through cache
  brands_total?: number;
  searches_total?: number;
  searches_ok?: number;
  searches_failed?: number;
  // (store x candidate) root searches -- the stable planned denominator (~= brands x
  // stores) that reaches 100%; searches_* above count every page + retry attempt.
  search_units_total?: number;
  search_units_ok?: number;
  search_units_failed?: number;
  products_discovered?: number;
  // false for search-only platforms (Instamart): no PDP enrich stage,
  // so the run view hides the PDP bar/rows + the "Advance to PDP" button. Undefined
  // on other platforms/older runs => treated as having a PDP stage.
  has_pdp_stage?: boolean;
  pdps_total?: number;
  pdps_done?: number;
  pdps_failed?: number;
  cache_search_hits?: number;
  cache_search_misses?: number;
  cache_pdp_hits?: number;
  cache_pdp_misses?: number;
  skus_added?: number;
  skus_removed?: number;
  skus_reactivated?: number;
  // store sampling (planning/23 [01]/[11]). sample_mode is the EFFECTIVE resolved mode
  // ("full"/"sampled") -- use it, not run.knobs.mode, so legacy knobs={} jobs still label.
  // remove_missing = whether this run actually retired SKUs (a complete full run, or a
  // sampled run whose rolling window covered every store); false => additive by design.
  sample_mode?: string;
  sample_k?: number;
  active_stores_total?: number;
  remove_missing?: boolean;
  sweep_complete?: boolean;
  rolling_window_runs?: number;
  // merchant sweep (Phase 9): geo cascade + serviceability cache
  stage?: string; // staged activity: "probing" | "anchoring"
  phase?: string; // coarse | mid | dense
  cells_total?: number;
  cells_probed?: number; // total probe operations (incl. retries) - throughput, not progress
  cells_succeeded?: number; // cells resolved via a successful probe (once each) - progress bar
  cells_serviceable?: number;
  cells_cached?: number; // cross-run read-through cache hits
  cells_reused?: number; // in-run dedupe reuse (serviceable parent re-hit across phases)
  probe_errors?: number;
  stores_found?: number;
  stores_new?: number;
  sightings?: number;
  stores_added?: number;
  stores_removed?: number;
  stores_reactivated?: number;
  // retry churn: live probe attempts bucketed by ServiceabilityOutcome (ok / unknown / blocked /
  // rate_limited / timeout / transport_error / other_4xx), plus requeued-attempt count.
  attempts?: Record<string, number>;
  retries?: number;
  // taxonomy classify (Phase 12): LLM classification of unlabelled SKUs into the canonical tree.
  total?: number; // active SKUs to classify this run
  // -- product_match --------------------------------------------------------------
  // Brand-partitioned, so the unit of progress is a brand, not a store or a request.
  brands?: number; // brands matched so far
  groups?: number; // resolved SKUs (a group of listings that are one product)
  cross_platform?: number; // of those, groups spanning more than one platform
  judged?: number; // pairs the model was asked to decide
  named?: number; // groups the model was asked to name (only ones whose identity has none)
  combos_excluded?: number; // bundles of different products held out before matching
  listings?: number; // platform listings read across every platform
  classified?: number; // mapped to a leaf
  unmapped?: number; // legitimately no-fit (persisted with node_id '')
  unknown?: number; // omitted by the model after retries (left unlabelled -> retried next run)
  batches_done?: number;
  batches_total?: number;
  // shelf-id resolve: per-(store, SKU) sellable ids asked for one PDP at a time. The work
  // is a SET of pairs, not a store x SKU rectangle, so it carries its own total instead of
  // stores_total x skus. resolved + absent are the two ANSWERS (an id, or the platform
  // stating it has no shelf row); both are written, and their sum is rows_emitted.
  pairs_total?: number;
  pairs_resolved?: number;
  pairs_absent?: number;
  // The re-check cut of those same answers: pairs the pass already had an id for, and how
  // that id fared. changed + lost is the finding no other flow can produce - an id we are
  // still addressing that the platform has moved reads downstream as a delisting.
  pairs_rechecked?: number;
  pairs_changed?: number;
  pairs_lost?: number;
  pairs_restored?: number;
  // How the worklist split. Their sum is pairs_total. pairs_reask is non-zero only on a
  // full re-fetch (skip_existing off), which is the one run that can catch an id that moved.
  pairs_new?: number;
  pairs_reask?: number;
  // Failed asks that reached a 5xx. No batches_* name exists for it (CartOutcome is a
  // closed, cart-shaped set), so it has no churn-panel row and lives here only.
  asks_5xx?: number;
  // Asks the platform ANSWERED and whose body then refused us (a placeholder PDP served to
  // a session it will not serve). Kept apart from batches_transport_error, which asserts
  // the request never arrived: the two point at opposite causes and opposite fixes.
  asks_refused?: number;
  // The SEARCH prepass that runs ahead of the PDP pass, where the platform's search cards
  // already state the store-local id. Its own namespace rather than folded into the
  // batches_*/pairs_* counters: a search and a PDP ask different questions at different
  // prices, and one combined number would hide which pass saved the run and which spent it.
  search_stores_total?: number;
  search_stores_done?: number;
  search_stores_failed?: number;
  search_terms_searched?: number;
  search_sightings?: number;
  search_rows_written?: number;
  search_rotations?: number;
  search_failures?: number;
  // Pairs the prepass removed from the PDP worklist -- the difference between the worklist
  // read before it and after it. The only prepass number that is tracked-SKU-exact:
  // search_rows_written counts every id the cards stated, and a brand search returns that
  // brand's whole catalogue (roughly two thirds untracked), plus pairs already answered.
  search_pairs_cleared?: number;
  // Bandwidth split by the pass that spent it; their sum is bytes_up/bytes_down above.
  // The split is what settles the trade the prepass is a bet on -- one search page against
  // a PDP per pair -- which the lease and row counters alone cannot say.
  pdp_bytes_up?: number;
  pdp_bytes_down?: number;
  search_bytes_up?: number;
  search_bytes_down?: number;
  // search rank / category rank: the unit of work is a PROBE, one shelf asked at one dark
  // store, and its answer is a page of slots. It has no store x SKU rectangle at all, so
  // none of the store_sku_* counters above are ever written on a rank run - rendering them
  // reads 15 rows written of a 0-pair universe and a negative pending.
  probes_planned?: number;
  probes_resumed?: number; // answered by an earlier attempt of this run, not re-asked
  probes_done?: number;
  probes_ok?: number;
  probes_no_results?: number; // the platform has nothing for this term: an answer, not a gap
  probes_unknown_category?: number; // the category id no longer resolves
  probes_failed?: number;
  placements_written?: number; // slot rows, aliased to rows_emitted for the shared row floor
  // The two axes probes_planned is the product of, so the page can say what was asked.
  shelves_total?: number;
  // Of the probes, the share that reached an answer. Below coverage_threshold_pct the
  // run's shares are not comparable with the previous run's, which is what the run is for.
  coverage_pct?: number;
  coverage_threshold_pct?: number;
  // availability build / close / rebuild: no platform calls. A run is a sequence of
  // ClickHouse steps over every cart run of its platform that ended since the last one.
  runs_enriched?: number;
  days_rolled_up?: number;
  runs_reach_built?: number;
  reach_failed?: number;
  ch_rows_read?: number; // rows ClickHouse has read so far, published while a step runs
  remaining_days?: number; // a rebuild's days still to do after this run's
  step?: string; // the step running now
  step_ms?: Record<string, number>; // each finished step's duration
  step_rows?: Record<string, number>; // each finished step's rows
  failed_step?: string;
  day?: string; // the day a backfill or rebuild run did (ISO date)
  runs_without_reach?: number; // a backfill day's counted runs found without Reach
  skus_without_reach?: number; // and the products they carry
  noop?: boolean; // nothing to do: kept out of the Runs list by default
}

export interface CartSummary extends CartProgress {
  elapsed_seconds?: number;
  // set by the worker when a completed run is marked DEGRADED (why it fell short)
  degraded_reason?: string;
}

// Frontend-narrowed views of the generated read models: the backend keeps
// progress/summary/inputs as free-form dicts, so we override those (and the
// string status/trigger) with the richer frontend shapes at the call boundary.
export type Run = Omit<RunOut, "progress" | "summary" | "status" | "trigger"> & {
  status: RunStatus;
  trigger: Trigger;
  progress: CartProgress | null;
  summary: CartSummary | null;
};

export type Job = Omit<JobOut, "inputs" | "platform" | "flow"> & {
  inputs: JobInputs;
  platform: Platform;
  flow: Flow;
};

// -- store status vocabulary --------------------------------------------------
const STORE_STATUS_LABEL: Record<string, string> = {
  active: "Active",
  closed: "Closed",
};
export const storeStatusLabel = (s: string) => STORE_STATUS_LABEL[s] ?? s;
export function storeStatusTone(s: string): "ok" | "warn" | "crit" | "idle" {
  if (s === "active") return "ok";
  if (s === "closed") return "crit";
  return "idle";
}

// -- labels / vocabulary ------------------------------------------------------
// The platform list, labels, colours, and PDP-URL templates are served from the
// backend registry via GET /v1/platforms (see ./platforms). `platformLabel` and
// `pdpUrl` are re-exported here so existing call sites keep importing them from
// labels; the enumerable list is `platformKeys()` from ./platforms.
export { platformKeys, platformLabel, pdpUrl } from "./platforms";

const FLOW_LABEL: Record<string, string> = {
  cart_inventory: "Cart inventory",
  brand_sweep: "Brand sweep",
  merchant_sweep: "Merchant sweep",
  store_sku_map: "Store x SKU map",
  taxonomy_classify: "Taxonomy classify",
  // The operation, matching its siblings above. The brand-page tab that shows the RESULT
  // is "Matched SKUs" -- same split as this flow's Taxonomy classify / Taxonomy screen.
  product_match: "Product match",
  shelf_id_resolve: "Shelf id resolve",
  search_rank: "Search rank",
  category_rank: "Category rank",
  availability_build: "Availability build",
  availability_rebuild: "Availability rebuild",
  availability_backfill: "Availability backfill",
};
export const flowLabel = (f: string) => FLOW_LABEL[f] ?? f;

// What each flow is for: the grouping of the Jobs and Runs pages. Mirrors the backend's
// `category_of`, which the API also returns on each job.
export const FLOW_CATEGORY: Record<Flow, FlowCategory> = {
  cart_inventory: "availability",
  store_sku_map: "availability",
  availability_build: "availability",
  availability_rebuild: "availability",
  availability_backfill: "availability",
  merchant_sweep: "discovery",
  brand_sweep: "discovery",
  shelf_id_resolve: "discovery",
  search_rank: "rank",
  category_rank: "rank",
  taxonomy_classify: "catalog",
  product_match: "catalog",
};

export const CATEGORY_LABEL: Record<FlowCategory, string> = {
  availability: "Availability",
  discovery: "Discovery",
  rank: "Rank",
  catalog: "Catalog",
};

// The flows the control plane creates and queues itself: never created from the form.
export const MANAGED_FLOWS: ReadonlySet<Flow> = new Set<Flow>([
  "availability_build",
  "availability_rebuild",
  "availability_backfill",
]);

// The two jobs created per platform; the API refuses to delete them.
export const SEEDED_FLOWS: ReadonlySet<Flow> = new Set<Flow>([
  "availability_build",
  "availability_backfill",
]);

const STEP_LABEL: Record<string, string> = {
  compact: "Compact",
  cells: "Cells",
  enrich: "Enrich",
  rollup: "Roll up",
  close: "Close day",
  reach: "Reach",
};
export const stepLabel = (step: string) => STEP_LABEL[step] ?? step;
const STEP_ORDER = Object.keys(STEP_LABEL);
/** A run's finished steps in the order they run; the stored map's key order is not that. */
export const stepsInOrder = (stepMs: Record<string, number>): string[] =>
  Object.keys(stepMs).sort((a, b) => STEP_ORDER.indexOf(a) - STEP_ORDER.indexOf(b));

export const TERMINAL: RunStatus[] = [
  "succeeded",
  "degraded",
  "failed",
  "cancelled",
  "timed_out",
];
export const isTerminal = (s: RunStatus) => TERMINAL.includes(s);
export const isCancellable = (s: RunStatus) =>
  s === "queued" || s === "claimed" || s === "running";

// status -> pill class (one of the qcs pill tones)
export function statusTone(s: RunStatus): "ok" | "warn" | "crit" | "info" | "idle" {
  switch (s) {
    case "succeeded":
    case "running":
      return "ok";
    case "claimed":
    case "queued":
      return "info";
    case "degraded":
      return "warn";
    case "failed":
    case "timed_out":
      return "crit";
    case "cancelled":
      return "idle";
    default:
      return "idle";
  }
}

// -- formatters ---------------------------------------------------------------
export const fmtInt = (n: number | null | undefined) =>
  n == null ? "-" : Math.round(n).toLocaleString();

export function fmtRows(n: number | null | undefined): string {
  if (n == null) return "-";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} K`;
  return n.toLocaleString();
}

export function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "-";
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(2)} GB`;
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`;
  if (n >= 1 << 10) return `${(n / (1 << 10)).toFixed(1)} KB`;
  return `${n} B`;
}

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "-";
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${String(rem).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

const IST: Intl.DateTimeFormatOptions = { timeZone: "Asia/Kolkata", hour12: false };

// ClickHouse returns naive UTC timestamps ("YYYY-MM-DD HH:MM:SS" -- space separator, no
// zone). A browser parses those as *local* time, shifting every value by the local UTC
// offset (e.g. +5:30 in IST, so a row 20 min old reads as "5h ago"). Mark such strings as
// UTC before parsing. Strings that already carry a zone ('Z' or +/-HH:MM, e.g. Postgres
// tz-aware timestamps) or aren't a bare date-time are passed through untouched.
export function parseTs(iso: string): Date {
  const s = iso.trim();
  const hasZone = /([zZ]|[+-]\d\d:?\d\d)$/.test(s);
  if (!hasZone && /^\d{4}-\d\d-\d\d[ T]\d\d:\d\d/.test(s)) {
    return new Date(s.replace(" ", "T") + "Z");
  }
  return new Date(s);
}

export const fmtTime = (iso: string | null | undefined) =>
  iso ? parseTs(iso).toLocaleTimeString("en-GB", { ...IST }) + " IST" : "-";

export const fmtDate = (iso: string | null | undefined) =>
  iso
    ? parseTs(iso).toLocaleDateString("en-GB", {
        ...IST,
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

export const fmtDateTimeSec = (iso: string | null | undefined) =>
  iso
    ? parseTs(iso).toLocaleString("en-GB", {
        ...IST,
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }) + " IST"
    : "-";

export const fmtDateTime = (iso: string | null | undefined) =>
  iso
    ? parseTs(iso).toLocaleString("en-GB", {
        ...IST,
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }) + " IST"
    : "-";

export function ago(iso: string | null | undefined): string {
  if (!iso) return "-";
  const s = Math.max(0, (Date.now() - parseTs(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// elapsed seconds of a run: live (started->now) or terminal (summary.elapsed_seconds)
export function elapsedSeconds(run: Run): number | null {
  if (run.summary?.elapsed_seconds != null) return run.summary.elapsed_seconds;
  if (!run.started_at) return null;
  const end = run.finished_at ? parseTs(run.finished_at).getTime() : Date.now();
  return Math.max(0, (end - parseTs(run.started_at).getTime()) / 1000);
}

// throughput rows/s from the active counters + elapsed
export function throughput(stats: CartProgress, elapsed: number | null): number | null {
  if (elapsed == null || elapsed <= 0) return null;
  const rows = stats.rows_emitted;
  if (rows == null) return null;
  return rows / elapsed;
}

// Merchant-sweep throughput = live serviceability probe calls/s. Cache hits and in-run
// reuse make no network call, so they're excluded: live = cells_probed - cached - reused.
export function probeThroughput(stats: CartProgress, elapsed: number | null): number | null {
  if (elapsed == null || elapsed <= 0) return null;
  const probed = stats.cells_probed;
  if (probed == null) return null;
  const live = probed - (stats.cells_cached ?? 0) - (stats.cells_reused ?? 0);
  return Math.max(0, live) / elapsed;
}

// -- pipeline observability (cart flows) --------------------------------------
// Two axes, deliberately not merged. The CRAWLER axis counts what we asked of the flow
// (batch attempts, their outcomes, the retries they cost); the TARGET axis counts what the
// platform's domain actually served (HTTP requests, refusals, latency). They differ by
// construction -- one attempt can spend several requests, and a prime spends a request
// with no attempt behind it -- so a single "requests" number would be wrong for one of
// them. Every helper returns null (never 0) when the counter it needs is absent, so a run
// from before these counters shows "-" rather than a confident zero.

/** Network attempts the crawler made: every batch that reached the wire, retries included.
 *  Excludes ``skipped`` (never sent) -- it spent nothing, and counting it deflates rates. */
export function crawlerAttempts(stats: CartProgress): number {
  return (
    (stats.batches_ok ?? 0) +
    (stats.batches_not_serviceable ?? 0) +
    (stats.batches_empty ?? 0) +
    (stats.batches_blocked ?? 0) +
    (stats.batches_rate_limited ?? 0) +
    (stats.batches_other_4xx ?? 0) +
    (stats.batches_transport_error ?? 0) +
    (stats.batches_timeout ?? 0) +
    (stats.batches_proxy_provider_denied ?? 0)
  );
}

/** Attempts the adapter parsed into a usable answer (a cart, or a clean not-serviceable). */
export function crawlerSucceeded(stats: CartProgress): number {
  return (stats.batches_ok ?? 0) + (stats.batches_not_serviceable ?? 0);
}

// The refusal-share bands the engine sorts exit nodes into, mirroring
// PROXY_DEGRADED_REFUSAL_SHARE / PROXY_BURNED_REFUSAL_SHARE in core/flows/cart/types.py.
// Named as RANGES rather than as verdicts ("clean", "degraded", "burned") because a
// verdict has to be looked up before it means anything, and the range never does: a reader
// who has never seen this panel can still say what puts a node in each band.
export const PROXY_REFUSAL_BANDS = {
  clean: "0-20% refused",
  degraded: "20-50% refused",
  burned: "over 50% refused",
} as const;

/** One slice of a composition bar: a count, what to call it, and its semaphore tone. */
export type Slice = { key: string; label: string; n: number; tone: string };

/**
 * Every attempt, split by how it ended -- exhaustive by construction, so the slices sum
 * to ``crawlerAttempts`` and the bar always closes.
 *
 * A bar rather than one percentage row per outcome: four percentages make the reader add
 * them up to check nothing is missing, where a bar shows the composition, shows that it
 * closes, and shows which slice dominates, in one look.
 */
export function attemptComposition(stats: CartProgress): Slice[] {
  return [
    { key: "answered", label: "answered", tone: "ok", n: crawlerSucceeded(stats) },
    {
      key: "refused",
      label: "refused 4xx",
      tone: "crit",
      n: (stats.batches_blocked ?? 0) + (stats.batches_rate_limited ?? 0) + (stats.batches_other_4xx ?? 0),
    },
    { key: "shed", label: "answered with nothing", tone: "idle", n: stats.batches_empty ?? 0 },
    {
      key: "failed",
      label: "failed",
      tone: "warn",
      n:
        (stats.batches_transport_error ?? 0) +
        (stats.batches_timeout ?? 0) +
        (stats.batches_proxy_provider_denied ?? 0),
    },
  ].filter((slice) => slice.n > 0);
}

/**
 * Every call, split by whether it was allowed to be the question we came to ask.
 *
 * Only the split the counters measure exactly. A finer cut -- which calls were spent on
 * batches that were then refused -- would need the engine to tally calls per outcome
 * class; inferring it from latency here would put a guess on the same footing as a
 * measurement, in the one panel whose whole job is to be checkable.
 */
export function callComposition(stats: CartProgress): Slice[] {
  const total = stats.http_requests;
  if (total == null) return [];
  const before = stats.http_requests_setup ?? 0;
  return [
    { key: "work", label: "asked for products", tone: "ok", n: Math.max(0, total - before) },
    { key: "before", label: "spent before we could ask", tone: "warn", n: before },
  ].filter((slice) => slice.n > 0);
}

/** A share of ``crawlerAttempts`` as a percentage, or null when nothing has been sent. */
export function attemptShare(stats: CartProgress, n: number | undefined): number | null {
  const total = crawlerAttempts(stats);
  if (total <= 0 || n == null) return null;
  return (n / total) * 100;
}

// -- per-try economics --------------------------------------------------------
// How many attempts a pair cost before the platform answered it. The one quality signal
// that batch counters cannot produce: a batch on its third try can be answering one of its
// pairs for the first time, so "attempts" at the batch level says nothing about the pair.

// Bucket order for display. The backend grows keys as they occur, so a run that never
// needed a retry carries only "1" -- which is the healthiest shape, not missing data.
export const ANSWER_ATTEMPT_BUCKETS = ["1", "2", "3-5", "6-10", "11+"] as const;

/** Pairs the platform answered this run, at any cost. */
export function answersTotal(stats: CartProgress): number {
  return Object.values(stats.answers_by_attempts ?? {}).reduce((a, b) => a + b, 0);
}

/** Share of answers that took ONE attempt, or null on a run that has answered nothing.
 *
 *  Distinct from goodput, and more demanding: goodput asks whether an attempt was usable,
 *  this asks whether the pair needed a second one at all. A run can sit at 90% goodput and
 *  40% first-try, which is the same coverage bought with 2.5x the requests. */
export function firstTryRate(stats: CartProgress): number | null {
  const total = answersTotal(stats);
  if (total <= 0) return null;
  return ((stats.answers_by_attempts?.["1"] ?? 0) / total) * 100;
}

/** Mean attempts per answered pair. A true mean, not a bucket midpoint. */
export function meanAttemptsPerAnswer(stats: CartProgress): number | null {
  const total = answersTotal(stats);
  if (total <= 0 || stats.answer_attempts_total == null) return null;
  return stats.answer_attempts_total / total;
}

/** Of the pairs that needed a retry and then ENDED, the share the retries recovered.
 *
 *  Denominator is recovered + gave-up, not the requeue count: a requeue is a batch-level
 *  event and a batch carries many pairs, so dividing pairs by requeues would compare two
 *  different units. Near zero means the attempt budget is being spent on pairs that were
 *  never going to answer -- the case for lowering max_attempts_per_sku. */
export function retryEffectiveness(stats: CartProgress): number | null {
  const answers = stats.answers_by_attempts ?? {};
  const recovered = Object.entries(answers)
    .filter(([bucket]) => bucket !== "1")
    .reduce((a, [, n]) => a + n, 0);
  const ended = recovered + (stats.store_sku_giveup ?? 0);
  if (ended <= 0) return null;
  return (recovered / ended) * 100;
}

/**
 * The calls a batch's own work cost, with session setup netted out. Setup is a per-lease
 * cost, so it belongs to the lease, not to whichever batch happened to open it.
 */
export function workRequests(stats: CartProgress): number | null {
  if (stats.http_requests == null) return null;
  return Math.max(0, stats.http_requests - (stats.http_requests_setup ?? 0));
}

/**
 * Rows written per HTTP call -- what one call to the target actually buys.
 *
 * This way up rather than calls-per-row because the panel's other ratios all point the
 * same way (higher is more, and more is better), and because it is the direction the
 * number is naturally read in: one cart call answers for a whole chunk of ids, so "2.5
 * rows a call" is a yield, where "0.39 calls a row" is a fraction of a thing nobody
 * spends a fraction of.
 */
export function rowsPerRequest(stats: CartProgress): number | null {
  const calls = stats.http_requests ?? 0;
  if (calls <= 0 || stats.rows_emitted == null) return null;
  return stats.rows_emitted / calls;
}

// The outcome classes the engine buckets latency under, in reading order. "served" and
// "refused" carry the finding the split exists for; the rest are listed so a class that
// dominates a run cannot hide behind "other".
export const LATENCY_CLASSES = ["served", "refused", "shed", "failed", "denied"] as const;
export type LatencyClass = (typeof LATENCY_CLASSES)[number];

export const LATENCY_CLASS_HELP: Record<string, string> = {
  served: "A parsed answer. Slow here means the target is throttling us, not refusing us.",
  refused: "403 / 429 / other 4xx. Fast here is the signature of an edge refusal - the WAF answers before the origin is ever asked.",
  shed: "A 2xx that carried no cart: the target answered with nothing.",
  failed: "A dead tunnel, a timeout, or a 5xx. Latency here is mostly the timeout itself.",
  denied: "Our proxy provider refused the tunnel (402/407). Never reached the target.",
};

/** One histogram bucket, parsed out of its "<class>|<edge>" key. */
type LatencyBucket = { cls: string; edge: number; n: number };

/** Parse the histogram into typed buckets, ascending by edge.
 *
 *  Numeric edges ascending with the "inf" overflow last: object key order is not
 *  guaranteed to be the order the backend wrote, and a mis-ordered walk reports the wrong
 *  bucket rather than failing. A key with no class separator is read as class "unknown" --
 *  runs recorded before the split still percentile correctly, they just cannot be sliced. */
function latencyBuckets(stats: CartProgress): LatencyBucket[] {
  const raw = stats.latency_buckets_ms ?? {};
  return Object.entries(raw)
    .map(([key, n]) => {
      const sep = key.indexOf("|");
      const cls = sep < 0 ? "unknown" : key.slice(0, sep);
      const edgeText = sep < 0 ? key : key.slice(sep + 1);
      const edge = edgeText === "inf" ? Number.POSITIVE_INFINITY : Number(edgeText);
      return { cls, edge, n: n ?? 0 };
    })
    .filter((b) => !Number.isNaN(b.edge))
    .sort((a, b) => a.edge - b.edge);
}

/** The classes this run actually recorded, in LATENCY_CLASSES order (unknown last). */
export function latencyClasses(stats: CartProgress): string[] {
  const seen = new Set(latencyBuckets(stats).filter((b) => b.n > 0).map((b) => b.cls));
  const known = LATENCY_CLASSES.filter((c) => seen.has(c)) as string[];
  return [...known, ...[...seen].filter((c) => !known.includes(c))];
}

/** How many ATTEMPTS a class sampled -- one timing per attempt, whatever it fanned out to.
 *  The histogram is exhaustive, so its buckets sum to this. */
export function latencySamples(stats: CartProgress, cls?: string): number {
  return latencyBuckets(stats)
    .filter((b) => cls == null || b.cls === cls)
    .reduce((a, b) => a + b.n, 0);
}

/** Mean latency (ms) of one ATTEMPT, overall or for one outcome class; null if nothing sampled.
 *
 *  Per attempt, not per request: an attempt that fans out reports the wall time of the whole
 *  sequence, so on a platform with fan-out this is several requests deep. Divide by the
 *  fan-out for a per-request figure. */
export function latencyMean(stats: CartProgress, cls?: string): number | null {
  if (cls != null) {
    const total = stats.latency_ms_total_by_class?.[cls];
    const n = latencySamples(stats, cls);
    return total == null || n <= 0 ? null : total / n;
  }
  const n = stats.latency_samples ?? latencySamples(stats);
  if (n <= 0 || stats.latency_ms_total == null) return null;
  return stats.latency_ms_total / n;
}

/** The inclusive upper edge (ms) of the bucket the ``p``th percentile request falls in,
 *  or null when no request was sampled. Infinity for the overflow bucket. ``cls`` narrows
 *  it to one outcome class.
 *
 *  A bucket bound, NOT an interpolated percentile: the histogram knows only how many
 *  attempts landed under each edge, and inventing a value inside a bucket would report a
 *  precision the counters do not have. Read it as "95% of attempts came back in <= this". */
export function latencyPercentile(stats: CartProgress, p: number, cls?: string): number | null {
  const buckets = latencyBuckets(stats).filter((b) => cls == null || b.cls === cls);
  const total = buckets.reduce((a, b) => a + b.n, 0);
  if (total <= 0) return null;
  const target = (p / 100) * total;
  let seen = 0;
  for (const b of buckets) {
    seen += b.n;
    if (seen >= target) return b.edge;
  }
  return buckets[buckets.length - 1].edge;
}

/** Per-second rate of a cumulative counter over the whole run, or null if it can't be. */
export function perSecond(n: number | undefined, elapsed: number | null): number | null {
  if (n == null || elapsed == null || elapsed <= 0) return null;
  return n / elapsed;
}

export function fmtRate(n: number | null | undefined): string {
  if (n == null) return "-";
  if (n >= 100) return `${Math.round(n)}/s`;
  if (n >= 10) return `${n.toFixed(1)}/s`;
  return `${n.toFixed(2)}/s`;
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null) return "-";
  // A genuine zero reads as "0%", never "0.0%": the decimal implies a measurement fine
  // enough to have rounded something away, and nothing was rounded.
  if (n === 0) return "0%";
  if (n < 0.1) return "<0.1%";
  return `${n < 10 ? n.toFixed(1) : Math.round(n)}%`;
}

export function fmtMs(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  // Two decimals under 10s: the slots resolve to 25ms, and "3.4s" would throw that away
  // for exactly the readings a tail investigation cares about.
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 2)}s`;
  return `${Math.round(n)}ms`;
}

/** The largest finite bucket edge (ms) the run's histogram carries, for rendering the
 *  overflow bucket as "> that" instead of an infinity the reader can't size. */
export function latencyTopEdge(stats: CartProgress): number | null {
  const edges = latencyBuckets(stats)
    .map((b) => b.edge)
    .filter((e) => Number.isFinite(e));
  return edges.length > 0 ? Math.max(...edges) : null;
}

// The engine records latency in fixed 50ms slots, so the bucket a percentile lands in is
// narrow enough to report its MIDPOINT as the measurement: within +/-25ms of the true
// value, which is a number rather than a range.
export const LATENCY_SLOT_MS = 50;

// The log-spaced edges the histogram used before the fixed-width slots landed. A run
// recorded then cannot be read to 25ms - its p50 genuinely is "somewhere at or under 5s" -
// so those are still rendered as a bound, and only those. (A new-format run whose every
// sample happened to land exactly on one of these would be misread as legacy and shown as
// a bound: strictly less precision, never a wrong number.)
const LEGACY_EDGES_MS = new Set([100, 250, 500, 1000, 2000, 5000, 10000]);

function isLegacyHistogram(stats: CartProgress): boolean {
  const edges = latencyBuckets(stats)
    .filter((b) => b.n > 0 && Number.isFinite(b.edge))
    .map((b) => b.edge);
  return edges.length > 0 && edges.every((e) => LEGACY_EDGES_MS.has(e));
}

/**
 * A percentile as the reader should see it: the measured value ("3.42s"), or a bound
 * ("<=5.0s" / ">30s") where the counters genuinely cannot say more than that.
 *
 * Bounded in exactly two cases, and it says which by keeping the "<=" / ">": a run
 * recorded before the fixed-width slots, and a percentile that falls past the histogram's
 * last slot. Everything else is a number.
 */
export function fmtLatencyPercentile(stats: CartProgress, p: number, cls?: string): string {
  const v = latencyPercentile(stats, p, cls);
  if (v == null) return "-";
  if (!Number.isFinite(v)) {
    const top = latencyTopEdge(stats);
    return top == null ? "-" : `>${fmtMs(top)}`;
  }
  if (isLegacyHistogram(stats)) return `<=${fmtMs(v)}`;
  return fmtMs(v - LATENCY_SLOT_MS / 2);
}
